import { useAtom, useAtomValue } from 'jotai';
import React, { Activity, useEffect, useRef, useState } from 'react';

import { DrawerEvents, EmbeddedDrawer } from '@crossnokaye/ux-common/ckds';

import { EquipmentBulkEditDrawer } from './equipment-bulk-edit-drawer';
import { EquipmentDetailsDrawer } from './equipment-details-drawer';
import { drawerIsOpenAtom, DrawerType, drawerTypeAtom } from './state/drawer.state';
import { useOpenBulkEdit } from '../equipment-bulk-edit/hooks/use-open-bulk-edit';
import { pushOutOpenAtom } from '../equipment-bulk-edit/state/bulk-edit-params.state';
import { useEquipmentDetailsRouteState } from '../equipment-details/hooks/use-equipment-details-route-state';

type EquipmentDrawerLoaderProps = {
  children?: React.ReactNode;
};

export const EquipmentDrawerLoader = ({
  children,
}: EquipmentDrawerLoaderProps): React.ReactNode => {
  const { closeEquipmentDetails, isDetailsOpen } = useEquipmentDetailsRouteState();
  const [isBulkEditOpen, setIsBulkEditOpen] = useAtom(drawerIsOpenAtom);
  const drawerType = useAtomValue(drawerTypeAtom);
  const [detailsDrawerOpen, setDetailsDrawerOpen] = useState(isDetailsOpen);
  const [isVisible, setIsVisible] = useState(false);
  const [visibleDrawerType, setVisibleDrawerType] = useState<DrawerType | undefined>(undefined);
  const closingDrawerTypeRef = useRef<DrawerType | undefined>(undefined);

  const isPushOutOpen = useAtomValue(pushOutOpenAtom);
  const { clearBulkEditAction } = useOpenBulkEdit();

  useEffect(() => {
    setDetailsDrawerOpen(isDetailsOpen);
  }, [isDetailsOpen]);

  useEffect(() => {
    if (detailsDrawerOpen && isBulkEditOpen && drawerType === 'bulk-edit') {
      clearBulkEditAction();
    }
  }, [detailsDrawerOpen, isBulkEditOpen, drawerType, clearBulkEditAction]);

  const activeDrawerType: DrawerType | undefined = detailsDrawerOpen
    ? 'details'
    : isBulkEditOpen && drawerType === 'bulk-edit'
      ? 'bulk-edit'
      : undefined;
  const drawerIsControlledOpen = Boolean(activeDrawerType);
  const isDrawerMounted = drawerIsControlledOpen || isVisible;
  const renderedDrawerType = activeDrawerType ?? visibleDrawerType;

  const closeDrawerAction = () => {
    closingDrawerTypeRef.current = activeDrawerType;
    if (activeDrawerType === 'details') {
      setDetailsDrawerOpen(false);
      void closeEquipmentDetails();
      return;
    }

    if (activeDrawerType === 'bulk-edit') {
      setIsBulkEditOpen(false);
    }
  };

  useEffect(() => {
    if (activeDrawerType) {
      setIsVisible(true);
      setVisibleDrawerType(activeDrawerType);
    }
  }, [activeDrawerType]);

  const handleDrawerClose = (event: (typeof DrawerEvents)[keyof typeof DrawerEvents]) => {
    if (event === DrawerEvents.CLOSE_COMPLETE) {
      const closingDrawerType = closingDrawerTypeRef.current ?? visibleDrawerType;
      setIsVisible(false);
      setVisibleDrawerType(undefined);
      closingDrawerTypeRef.current = undefined;

      if (closingDrawerType === 'bulk-edit') {
        clearBulkEditAction();
      }
    }
  };

  return (
    <>
      <Activity mode={isDrawerMounted ? 'visible' : 'hidden'}>
        <EmbeddedDrawer
          open={drawerIsControlledOpen}
          options={{ closeOnEscape: !isPushOutOpen }}
          onClose={closeDrawerAction}
          onDrawerEvent={handleDrawerClose}
        >
          {renderedDrawerType === 'details' && (
            <EquipmentDetailsDrawer onClose={closeDrawerAction} />
          )}
          {renderedDrawerType === 'bulk-edit' && (
            <EquipmentBulkEditDrawer onClose={async () => closeDrawerAction()} />
          )}
        </EmbeddedDrawer>
      </Activity>

      <Activity mode={isDrawerMounted ? 'hidden' : 'visible'}>{children}</Activity>
    </>
  );
};
